import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, AlertOctagon, Info,
  ChevronRight, RefreshCw, X, BellOff, Bell, Check
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const MUTE_DURATIONS = [
  { hours: 24, label: '1 day' },
  { hours: 24 * 7, label: '7 days' },
  { hours: 24 * 30, label: '30 days' },
  { hours: 24 * 365, label: '1 year' },
];

/**
 * SecurityAlertBadge — Surfaces latest daily/weekly audit alerts on the Admin dashboard.
 * Features: severity-coded banner, modal with detail, per-alert mute (snooze) with management.
 */
const SecurityAlertBadge = ({ token, onJumpToCompliance }) => {
  const [reports, setReports] = useState({ daily: null, weekly: null });
  const [mutes, setMutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [muteSelections, setMuteSelections] = useState({});  // alertKey -> hours
  const [muting, setMuting] = useState({});                  // alertKey -> bool
  const [showMutesPanel, setShowMutesPanel] = useState(false);
  const { toast } = useToast();

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [dailyRes, weeklyRes, mutesRes] = await Promise.all([
        axios.get(`${API}/api/mybookie/admin/security-reports?report_type=daily&limit=1`, authHeaders),
        axios.get(`${API}/api/mybookie/admin/security-reports?report_type=weekly&limit=1`, authHeaders),
        axios.get(`${API}/api/mybookie/admin/security-alerts/mutes`, authHeaders),
      ]);
      setReports({
        daily: (dailyRes.data.reports || [])[0] || null,
        weekly: (weeklyRes.data.reports || [])[0] || null,
      });
      setMutes(mutesRes.data.mutes || []);
    } catch (err) {
      console.error('SecurityAlertBadge fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRefresh = () => { setRefreshing(true); fetchAll(); };

  // Client-side signature: must match backend (utils/soc2.py:alert_signature)
  // Strip severity prefix → strip digits/% → lowercase → SHA1 first 16 hex chars.
  const computeSignature = useCallback(async (message) => {
    if (!message) return '';
    const m = message.match(/^(CRITICAL|HIGH|MEDIUM|LOW|INFO)\s*:\s*(.+)$/i);
    const body = m ? m[2] : message;
    const canonical = body.toLowerCase().replace(/[0-9.%]+/g, '*').replace(/\s+/g, ' ').trim();
    const buf = new TextEncoder().encode(canonical);
    const hash = await crypto.subtle.digest('SHA-1', buf);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
  }, []);

  const mutedSignatures = useMemo(() => new Set(mutes.map(m => m.signature)), [mutes]);

  // Parse + compute signatures for current report alerts
  const [parsedAlerts, setParsedAlerts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const all = [];
      const collect = (report) => {
        if (!report?.alerts?.length) return;
        report.alerts.forEach((rawMsg) => {
          const m = rawMsg.match(/^(CRITICAL|HIGH|MEDIUM|LOW|INFO):\s*(.+)$/i);
          all.push({
            rawMessage: rawMsg,
            severity: m ? m[1].toLowerCase() : 'info',
            message: m ? m[2] : rawMsg,
            source: report.type,
            timestamp: report.timestamp,
          });
        });
      };
      collect(reports.daily);
      collect(reports.weekly);
      const withSigs = await Promise.all(all.map(async a => ({ ...a, signature: await computeSignature(a.rawMessage) })));
      if (!cancelled) {
        const order = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
        setParsedAlerts(withSigs.sort((a, b) => (order[a.severity] ?? 5) - (order[b.severity] ?? 5)));
      }
    })();
    return () => { cancelled = true; };
  }, [reports, computeSignature]);

  const visibleAlerts = useMemo(
    () => parsedAlerts.filter(a => !mutedSignatures.has(a.signature)),
    [parsedAlerts, mutedSignatures]
  );

  const counts = useMemo(() => {
    const c = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    visibleAlerts.forEach(a => { c[a.severity] = (c[a.severity] || 0) + 1; });
    return c;
  }, [visibleAlerts]);

  const topSeverity = useMemo(() => {
    if (counts.critical > 0) return 'critical';
    if (counts.high > 0) return 'high';
    if (counts.medium > 0) return 'medium';
    if (counts.low > 0) return 'low';
    return 'clean';
  }, [counts]);

  const handleMute = async (alert, key) => {
    const hours = muteSelections[key] || 24 * 7;
    setMuting({ ...muting, [key]: true });
    try {
      await axios.post(
        `${API}/api/mybookie/admin/security-alerts/mute`,
        { message: alert.rawMessage, hours, reason: 'Muted from admin dashboard' },
        authHeaders
      );
      toast({
        title: 'Alert muted',
        description: `Snoozed for ${MUTE_DURATIONS.find(d => d.hours === hours)?.label || `${hours}h`}`,
      });
      await fetchAll();
    } catch (err) {
      toast({
        title: 'Mute failed',
        description: err.response?.data?.detail || 'Could not mute alert',
        variant: 'destructive',
      });
    } finally {
      setMuting({ ...muting, [key]: false });
    }
  };

  const handleUnmute = async (signature) => {
    try {
      await axios.delete(`${API}/api/mybookie/admin/security-alerts/mute/${signature}`, authHeaders);
      toast({ title: 'Alert unmuted', description: 'You will see this alert again in the next audit.' });
      await fetchAll();
    } catch (err) {
      toast({
        title: 'Unmute failed',
        description: err.response?.data?.detail || 'Could not unmute alert',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#5C6661] py-2" data-testid="security-badge-loading">
        <RefreshCw className="w-3 h-3 animate-spin" /> Checking security status...
      </div>
    );
  }

  // Severity styling
  const styles = {
    critical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-900', accent: 'text-red-700', icon: AlertOctagon, label: 'Critical' },
    high:     { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-900', accent: 'text-orange-700', icon: ShieldAlert, label: 'High' },
    medium:   { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', accent: 'text-amber-700', icon: AlertTriangle, label: 'Medium' },
    low:      { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-900', accent: 'text-blue-700', icon: Info, label: 'Low' },
  };

  // Clean state: small green pill, no banner. Still allow opening modal to manage mutes.
  if (topSeverity === 'clean') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium"
          data-testid="security-badge-clean"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          All security checks passing
          <button
            onClick={handleRefresh}
            className="ml-1 text-emerald-600 hover:text-emerald-900"
            aria-label="Refresh security status"
            data-testid="security-badge-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {mutes.length > 0 && (
          <button
            onClick={() => { setShowModal(true); setShowMutesPanel(true); }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[#5C6661] hover:text-[#0A2B1D] underline"
            data-testid="security-badge-mutes-link"
          >
            <BellOff className="w-3 h-3" /> {mutes.length} muted
          </button>
        )}
        {/* Reuse modal markup */}
        <ManagementModal
          open={showModal}
          onOpenChange={setShowModal}
          alerts={visibleAlerts}
          mutes={mutes}
          styles={styles}
          muteSelections={muteSelections}
          setMuteSelections={setMuteSelections}
          muting={muting}
          handleMute={handleMute}
          handleUnmute={handleUnmute}
          showMutesPanel={showMutesPanel}
          setShowMutesPanel={setShowMutesPanel}
          reports={reports}
          onJumpToCompliance={onJumpToCompliance}
        />
      </div>
    );
  }

  const s = styles[topSeverity];
  const Icon = s.icon;
  const totalAlerts = visibleAlerts.length;

  // Build summary chip list (shows non-zero severities)
  const chips = ['critical', 'high', 'medium', 'low']
    .filter((sev) => counts[sev] > 0)
    .map((sev) => ({ severity: sev, count: counts[sev], label: styles[sev].label }));

  return (
    <>
      <Card
        className={`${s.bg} ${s.border} border-2 p-4 sm:p-5`}
        data-testid="security-alert-banner"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start sm:items-center gap-3">
            <Icon className={`w-6 h-6 ${s.accent} flex-shrink-0 mt-0.5 sm:mt-0`} />
            <div>
              <div className={`font-semibold ${s.text}`} data-testid="security-banner-headline">
                {totalAlerts} security alert{totalAlerts === 1 ? '' : 's'} need{totalAlerts === 1 ? 's' : ''} attention
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {chips.map(({ severity, count, label }) => (
                  <span
                    key={severity}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[severity].text} bg-white/60 border ${styles[severity].border}`}
                    data-testid={`security-chip-${severity}`}
                  >
                    {count} {label}
                  </span>
                ))}
                {mutes.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-[#5C6661] bg-white/60 border border-[#E6E8E6]">
                    <BellOff className="w-3 h-3" /> {mutes.length} muted
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:flex-shrink-0">
            <Button variant="outline" size="sm" className="bg-white" onClick={handleRefresh} disabled={refreshing} data-testid="security-banner-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" className="bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm" onClick={() => setShowModal(true)} data-testid="security-banner-view-details">
              View details <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </Card>

      <ManagementModal
        open={showModal}
        onOpenChange={setShowModal}
        alerts={visibleAlerts}
        mutes={mutes}
        styles={styles}
        muteSelections={muteSelections}
        setMuteSelections={setMuteSelections}
        muting={muting}
        handleMute={handleMute}
        handleUnmute={handleUnmute}
        showMutesPanel={showMutesPanel}
        setShowMutesPanel={setShowMutesPanel}
        reports={reports}
        onJumpToCompliance={onJumpToCompliance}
      />
    </>
  );
};

// Modal extracted as a sibling to avoid duplication between alert-state and clean-state branches
const ManagementModal = ({
  open, onOpenChange, alerts, mutes, styles,
  muteSelections, setMuteSelections, muting,
  handleMute, handleUnmute,
  showMutesPanel, setShowMutesPanel,
  reports, onJumpToCompliance,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="security-alert-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-[#0A2B1D]" />
          Security Audit — {showMutesPanel ? 'Manage Muted Alerts' : 'Outstanding Findings'}
        </DialogTitle>
        <DialogDescription>
          {showMutesPanel
            ? 'These alerts are currently snoozed and won\'t appear on the dashboard or trigger emails until they expire.'
            : 'Latest daily and weekly automated audit reports. Mute any alert you\'ve already addressed or accept as known state.'}
        </DialogDescription>
      </DialogHeader>

      {/* Toggle between alerts and mutes */}
      <div className="flex gap-2 my-3">
        <Button
          size="sm"
          variant={showMutesPanel ? 'outline' : 'default'}
          className={showMutesPanel ? '' : 'bg-[#0A2B1D] hover:bg-[#254D3D] text-white'}
          onClick={() => setShowMutesPanel(false)}
          data-testid="modal-tab-alerts"
        >
          <Bell className="w-3.5 h-3.5 mr-1.5" /> Active Alerts ({alerts.length})
        </Button>
        <Button
          size="sm"
          variant={showMutesPanel ? 'default' : 'outline'}
          className={showMutesPanel ? 'bg-[#0A2B1D] hover:bg-[#254D3D] text-white' : ''}
          onClick={() => setShowMutesPanel(true)}
          data-testid="modal-tab-mutes"
        >
          <BellOff className="w-3.5 h-3.5 mr-1.5" /> Muted ({mutes.length})
        </Button>
      </div>

      {!showMutesPanel ? (
        <div className="space-y-3" data-testid="modal-alerts-section">
          {alerts.length === 0 && (
            <div className="text-center py-8 text-[#5C6661] text-sm" data-testid="modal-no-alerts">
              <Check className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
              No active alerts. Everything looks clean.
            </div>
          )}
          {alerts.map((a, idx) => {
            const aStyle = styles[a.severity] || styles.low;
            const AIcon = aStyle.icon;
            const key = `${a.signature}-${idx}`;
            const selectedHours = muteSelections[key] || 24 * 7;
            return (
              <div
                key={key}
                className={`p-3 rounded-md ${aStyle.bg} ${aStyle.border} border`}
                data-testid={`security-alert-item-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <AIcon className={`w-5 h-5 ${aStyle.accent} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold uppercase ${aStyle.accent}`}>
                        {aStyle.label}
                      </span>
                      <span className="text-xs text-[#5C6661]">
                        {a.source === 'daily' ? 'Daily audit' : 'Weekly audit'} — {new Date(a.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className={`mt-1 text-sm ${aStyle.text}`}>{a.message}</p>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <Select
                        value={String(selectedHours)}
                        onValueChange={(v) => setMuteSelections({ ...muteSelections, [key]: parseInt(v, 10) })}
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs bg-white" data-testid={`mute-duration-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MUTE_DURATIONS.map((d) => (
                            <SelectItem key={d.hours} value={String(d.hours)}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMute(a, key)}
                        disabled={!!muting[key]}
                        className="h-8 text-xs bg-white"
                        data-testid={`mute-alert-${idx}`}
                      >
                        <BellOff className="w-3 h-3 mr-1.5" />
                        {muting[key] ? 'Muting...' : 'Mute'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2" data-testid="modal-mutes-section">
          {mutes.length === 0 && (
            <div className="text-center py-8 text-[#5C6661] text-sm" data-testid="modal-no-mutes">
              No muted alerts.
            </div>
          )}
          {mutes.map((m) => (
            <div
              key={m.signature}
              className="flex items-start gap-3 p-3 rounded-md bg-[#F4F1EA] border border-[#D5CFC1]"
              data-testid={`muted-alert-${m.signature}`}
            >
              <BellOff className="w-4 h-4 text-[#5C6661] flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#111614]">{m.message_sample}</p>
                <p className="text-xs text-[#5C6661] mt-1">
                  Muted by {m.muted_by} • Expires {new Date(m.expires_at).toLocaleString()}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-[#5C6661] hover:text-[#0A2B1D] h-8"
                onClick={() => handleUnmute(m.signature)}
                data-testid={`unmute-alert-${m.signature}`}
              >
                <Bell className="w-3 h-3 mr-1.5" /> Unmute
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center mt-6 pt-4 border-t border-[#E6E8E6]">
        <div className="text-xs text-[#5C6661]">
          {reports.daily && (<>Last daily: {new Date(reports.daily.timestamp).toLocaleString()}<br /></>)}
          {reports.weekly && (<>Last weekly: {new Date(reports.weekly.timestamp).toLocaleString()}</>)}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="security-modal-close">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
          {onJumpToCompliance && (
            <Button
              className="bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm"
              onClick={() => { onOpenChange(false); onJumpToCompliance(); }}
              data-testid="security-modal-jump-to-compliance"
            >
              Open compliance dashboard <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export { SecurityAlertBadge };
export default SecurityAlertBadge;
